import { useEffect, useState, useMemo } from "react";
import { api, formatDate, formatTime } from "@/lib/api";
import { TITANS_2026_BROADCAST_BY_WEEK } from "@/lib/networks";
import { toast } from "sonner";
import PlayoffOutlook from "@/pages/PlayoffOutlook";

const TITANS_LOGO = "https://a.espncdn.com/i/teamlogos/nfl/500/ten.png";

export default function Schedule() {
  const [events, setEvents] = useState([]);
  const [team, setTeam] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/schedule");
        setEvents(data.events || []);
        setTeam(data.team || null);
      } catch (e) {
        toast.error("Schedule failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "completed") return events.filter((e) => e.completed);
    if (filter === "upcoming") return events.filter((e) => !e.completed);
    if (filter === "preseason") return events.filter((e) => e.season_type === "preseason");
    if (filter === "regular") return events.filter((e) => e.season_type !== "preseason");
    return events;
  }, [events, filter]);

  return (
    <div data-testid="schedule-page" className="max-w-[1400px] mx-auto px-5 md:px-8 py-16">
      <div className="mb-10">
        <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">Season {team?.year || ""}</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide section-title">Schedule</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {[
          { k: "all", l: "All" },
          { k: "preseason", l: "Preseason" },
          { k: "regular", l: "Regular Season" },
          { k: "upcoming", l: "Upcoming" },
          { k: "completed", l: "Completed" },
        ].map((b) => (
          <button
            key={b.k}
            data-testid={`schedule-filter-${b.k}`}
            onClick={() => setFilter(b.k)}
            className={`font-cond uppercase tracking-wider text-sm px-5 py-2.5 border transition-colors ${
              filter === b.k
                ? "bg-titans-red border-titans-red text-white"
                : "border-[rgba(138,141,143,0.3)] text-titans-silver hover:text-white hover:border-white"
            }`}
          >
            {b.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-titans-silver">Loading schedule…</div>
      ) : (
        <>
          {/* Split into preseason + regular season stacked sections when the filter is "all". */}
          {(() => {
            const showPre = filter === "all" || filter === "preseason";
            const showReg = filter === "all" || filter === "regular" || filter === "upcoming" || filter === "completed";
            const preRows = filtered.filter((e) => e.season_type === "preseason");
            const regRows = filtered.filter((e) => e.season_type !== "preseason");
            const nextId = (events.find((e) => !e.completed) || {}).id;

            const renderRow = (ev, idx) => {
              const titansHome = ev.home?.abbr === "TEN";
              const titans = titansHome ? ev.home : ev.away;
              const opp = titansHome ? ev.away : ev.home;
              const won = ev.completed && (titansHome ? ev.home.winner : ev.away.winner);
              return (
                <div
                  key={ev.id}
                  data-testid={`schedule-row-${idx}`}
                  className={`titans-card hover-rise grid grid-cols-12 items-center gap-4 p-5 ${ev.id === nextId ? "glow-pulse" : ""}`}
                >
                  <div className="col-span-2 md:col-span-1">
                    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
                      {ev.season_type === "preseason" ? "Preseason" : "Week"}
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-display text-3xl text-white">{ev.week || "—"}</span>
                      {ev.season_type === "preseason" && (
                        <span className="bg-titans-red text-white font-cond uppercase tracking-wider text-[9px] px-1.5 py-0.5" data-testid={`schedule-row-${idx}-pre-chip`}>PRE</span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-10 md:col-span-3">
                    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">{titansHome ? "Home" : "Away"}</div>
                    <div className="flex items-center gap-3 mt-1">
                      <img src={TITANS_LOGO} alt="Titans" className="h-10 w-10 object-contain shrink-0" loading="lazy" decoding="async" />
                      <span className="font-display text-base uppercase text-titans-silver">{titansHome ? "vs" : "@"}</span>
                      {opp?.logo && (
                        <img src={opp.logo} alt={opp?.abbr || ""} className="h-10 w-10 object-contain shrink-0" loading="lazy" decoding="async" />
                      )}
                      <div className="font-display text-xl uppercase leading-tight">{opp?.name || opp?.abbr}</div>
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Date</div>
                    <div className="font-body text-white mt-1">{formatDate(ev.date)}</div>
                    <div className="font-body text-titans-silver text-xs">{formatTime(ev.date)}</div>
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    {ev.completed ? (
                      <>
                        <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Final</div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`font-display text-3xl ${won ? "text-titans-red" : "text-white"}`}>{titans?.score} - {opp?.score}</span>
                          <span className={`font-cond uppercase tracking-wider text-xs ${won ? "text-titans-red" : "text-titans-silver"}`}>{won ? "W" : "L"}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Status</div>
                        <div className="font-body text-white mt-1">{ev.status || "Scheduled"}</div>
                      </>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-2">
                    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Where to Watch</div>
                    {(() => {
                      const nets = ev.broadcast?.length ? ev.broadcast : [];
                      const fallbackNet = TITANS_2026_BROADCAST_BY_WEEK[ev.week];
                      if (!nets.length && fallbackNet) nets.push(fallbackNet);
                      if (!nets.length) return <div className="font-body text-titans-blue text-sm mt-1">TBD</div>;
                      return (
                        <ul className="mt-1 space-y-0.5" data-testid={`schedule-row-${idx}-broadcast`}>
                          {nets.map((n, i) => (<li key={i} className="font-body text-white text-sm leading-tight">{n}</li>))}
                        </ul>
                      );
                    })()}
                  </div>
                </div>
              );
            };

            return (
              <div className="space-y-10">
                {showPre && preRows.length > 0 && (
                  <section data-testid="schedule-section-preseason">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="bg-titans-red text-white font-cond uppercase tracking-wider text-[10px] px-2 py-1">Preseason</span>
                      <h2 className="font-display text-3xl md:text-4xl uppercase tracking-wide">2026 Preseason</h2>
                      <div className="flex-1 border-t border-titans-blue/30" />
                    </div>
                    <div className="space-y-3">{preRows.map((ev, i) => renderRow(ev, `pre-${i}`))}</div>
                  </section>
                )}
                {showReg && regRows.length > 0 && (
                  <section data-testid="schedule-section-regular">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="bg-titans-blue text-white font-cond uppercase tracking-wider text-[10px] px-2 py-1">Regular</span>
                      <h2 className="font-display text-3xl md:text-4xl uppercase tracking-wide">2026 Regular Season</h2>
                      <div className="flex-1 border-t border-titans-blue/30" />
                    </div>
                    <div className="space-y-3">{regRows.map((ev, i) => renderRow(ev, `reg-${i}`))}</div>
                  </section>
                )}
                {(preRows.length + regRows.length) === 0 && <div className="text-titans-silver">No games to show.</div>}
              </div>
            );
          })()}
        </>
      )}

      <PlayoffOutlook />
    </div>
  );
}
